import { FormattedMessage, useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import LeLiveHeading from './LeLiveHeading'
import Image from 'components/Common/Image'
import Sheet from 'components/utils/Sheet'
import LeLiveGetTicketsButton from './LeLiveGetTicketsButton'
import LayoutContainer from 'components/Common/LayoutContainer'
import styled from 'styled-components'
import { rem } from 'polished'
const prizesHorizontalBreakpoint = `@media (min-width: ${rem(1280)})`

const ImageContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: max-content;
  position: relative;
  min-height: ${rem(170)};

  ${prizesHorizontalBreakpoint} {
    grid-template-columns: max-content;
    flex: 1 1 0;
    min-width: 0;
    min-height: ${rem(340)};
  }
`

const FirstImage = styled.div`
  grid-column: 1;
  grid-row: 1;
  align-self: start;
  justify-self: start;
  width: 55%;
  transform: rotate(-3deg);
  border-radius: ${rem(8)};
  overflow: hidden;

  img {
    width: 100%;
    height: auto;
  }

  ${prizesHorizontalBreakpoint} {
    width: ${rem(370)};
  }
`

const SecondImage = styled.div`
  grid-column: 1;
  grid-row: 1;
  width: 60%;
  margin-left: 40%;
  margin-top: ${rem(32)};
  transform: rotate(3deg);
  border-radius: ${rem(8)};
  overflow: hidden;

  img {
    width: 100%;
    height: auto;
  }

  ${prizesHorizontalBreakpoint} {
    width: ${rem(412)};
    margin-left: ${rem(268)};
    margin-top: ${rem(65)};
  }
`

const PrizesLayout = styled(Group)`
  ${prizesHorizontalBreakpoint} {
    && {
      flex-direction: row;
    }
  }
`

const TextContainer = styled(Group)`
  text-align: center;

  ${prizesHorizontalBreakpoint} {
    max-width: ${rem(420)};
    text-align: left;
  }
`

const PrizesSection = styled(Sheet)`
  background-color: #f3f1ea;

  @media (min-width: ${rem(1400)}) {
    padding: ${rem(124)} ${rem(150)} ${rem(64)} ${rem(150)};
  }
`

interface Props {
  hasRegistration: boolean
}

function LeLivePrizes({ hasRegistration }: Props) {
  const intl = useIntl()

  return (
    <PrizesSection
      id="prizes"
      padding="64 20 40 20"
      desktopPadding="124 60 64 60"
    >
      <LayoutContainer variant="large">
        <PrizesLayout direction="vertical" horizontalAlign="center" gap={40}>
          <ImageContainer>
            <FirstImage>
              <Image
                id="arktiebljbmypp0wudn"
                alt={intl.formatMessage({
                  id: '_Bd1NR35ghtfwd-PI6hr',
                  defaultMessage: 'Travel prizes with hot air balloons',
                  description: 'Accessible alt text for the first decorative image in the prizes section on the LE Live page',
                })}
                width={370}
                height={254}
                dpr={2}
                enhancedFormats
              />
            </FirstImage>
            <SecondImage>
              <Image
                id="tx1c2l7lutm7eiwwtts"
                alt={intl.formatMessage({
                  id: 'lXmYWkQ-eqJ-XQkNafrl',
                  defaultMessage: 'Aerial view of tropical beach',
                  description: 'Accessible alt text for the second decorative image in the prizes section on the LE Live page',
                })}
                width={412}
                height={276}
                dpr={2}
                enhancedFormats
              />
            </SecondImage>
          </ImageContainer>

          <TextContainer direction="vertical" gap={20} verticalAlign="center" horizontalAlign="start">
            <LeLiveHeading variant="heading1alt" as="h2">
              <FormattedMessage
                id="69JEWqtLIUJB6tsfcGvv"
                defaultMessage="<em>Thousands</em> in travel prizes to give away"
                description="Main heading for the prizes section on the LE Live event page, with emphasis styling on the word Thousands"
                values={{
                  em: (chunks) => <em>{chunks}</em>,
                }}
              />
            </LeLiveHeading>
            <BodyText variant="large" colour="neutral-two">
              {intl.formatMessage({
                id: 'E98TpB9mJ4lmu7XlrkPh',
                defaultMessage: 'The world’s most inspiring destinations, exclusive show-only offers and unforgettable travel moments, all under one roof.',
                description: 'Prizes section subtitle describing what attendees can expect',
              })}
            </BodyText>
            <LeLiveGetTicketsButton hidden={hasRegistration} />
          </TextContainer>
        </PrizesLayout>
      </LayoutContainer>
    </PrizesSection>
  )
}

export default LeLivePrizes
